package org.moxultimate.topwithmox.product.details

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.BuyInOption
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.cards.ProductCard
import org.moxultimate.topwithmox.components.cards.ProductLabelType
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.product.data.ProductResponse
import org.moxultimate.topwithmox.statics.ProductStatics
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * ProductDetailPage
 * Entry composable for the /productdetail route.
 * Fetches a single product content entry by [contentId] and renders [ProductDetailContent].
 *
 * @param contentId ID of the product content entry to display.
 */
@Composable
fun ProductDetailPage(contentId: Long) {
    val viewModel: ProductDetailViewModel = koinViewModel()
    val state by viewModel.state.collectAsState()

    LaunchedEffect(contentId) { viewModel.loadContent(contentId) }

    when (val s = state) {
        is ProductDetailState.Loading -> LoadingIndicator()
        is ProductDetailState.Error   -> ErrorPage(
            errorCode = s.statusCode.toString(),
            error = s.error,
            onTryAgain = { viewModel.loadContent(contentId) },
        )
        is ProductDetailState.Success -> ProductDetailContent(content = s.content)
    }
}

/**
 * ProductDetailContent
 * Full scrollable layout: section heading with video button, then the products grid.
 *
 * @param content Fully loaded product content entry with its ranked products.
 */
@Composable
private fun ProductDetailContent(content: ProductContentResponse) {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM

    Column(
        modifier = if (isMobile)
            Modifier.fillMaxWidth().padding(leftRight = 16.px, topBottom = 24.px)
        else
            Modifier
                .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
                .padding(leftRight = 3.cssRem, topBottom = 32.px),
    ) {
        ProductsHeading(
            title = content.title,
            description = content.description.orEmpty(),
            isMobile = isMobile,
            onVideoReviewClick = {
                val link = content.videoLink
                if (!link.isNullOrBlank()) window.open(link, "_blank")
            },
        )
        ProductsGrid(
            products = content.products,
            isMobile = isMobile,
        )
    }
}

/**
 * ProductsHeading
 * Section heading: bold category title on the left, "Full Video Review" gradient button on
 * the right, description paragraph below.
 *
 * @param title Section title shown in bold dark text (e.g. "Best Gaming Mouse 2026").
 * @param description Short body text displayed below the title row.
 * @param isMobile True when rendering on a small screen — uses smaller font size.
 * @param onVideoReviewClick Callback fired when the "Full Video Review" button is pressed.
 */
@Composable
fun ProductsHeading(
    title: String,
    description: String,
    isMobile: Boolean,
    onVideoReviewClick: () -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(bottom = 24.px, top = 100.px),
        verticalArrangement = Arrangement.spacedBy(10.px),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Box(modifier = Modifier.weight(1f).padding(right = 12.px)) {
                SpanText(
                    text = title,
                    modifier = Modifier
                        .color(DarkTextColor)
                        .fontFamily(FontFamily)
                        .fontSize(if (isMobile) 16.px else clamp(min = 20.px, value = 1.8.vw, max = 26.px))
                        .fontWeight(FontWeight.Bold),
                )
            }
            GradientButton(
                text = if (isMobile) ProductStatics.HEADING_VIDEO_REVIEW_SHORT else ProductStatics.HEADING_VIDEO_REVIEW,
                buttonHeight = if (isMobile) 36 else 45,
                onClick = onVideoReviewClick,
            )
        }
        SpanText(
            text = description,
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 16.px)
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .fontSize(if (isMobile) 13.px else 14.px)
                .fontWeight(FontWeight.Normal),
        )
    }
}

/**
 * ProductsGrid
 * Wrapping flex row of [ProductCard] items mapped from [ProductResponse] data.
 * Mobile: each card fills full width. Desktop: exactly 3 cards per row using calc().
 *
 * @param products Ordered list of product responses for this content section.
 * @param isMobile True when rendering on a small screen.
 */
@Composable
fun ProductsGrid(
    products: List<ProductResponse>,
    isMobile: Boolean,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("flex-wrap", "wrap") },
        horizontalArrangement = Arrangement.spacedBy(24.px),
        verticalAlignment = Alignment.Top,
    ) {
        products.forEach { product ->
            ProductCard(
                imageSrc = product.imagePath.orEmpty(),
                brand = product.brand.orEmpty(),
                productName = product.name.orEmpty(),
                description = product.description.orEmpty(),
                pros = product.pros.toLineList(),
                cons = product.cons.toLineList(),
                label = product.label.toProductLabelType(),
                buyInOptions = product.toBuyInOptions(),
                modifier = if (isMobile)
                    Modifier.fillMaxWidth().margin(bottom = 24.px)
                else
                    Modifier
                        .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                        .margin(bottom = 24.px),
            )
        }
    }
}

/**
 * toLineList
 * Splits a " | "-delimited pros/cons string into a trimmed, non-blank list.
 * Returns an empty list when the receiver is null or blank.
 */
internal fun String?.toLineList(): List<String> =
    this?.split(" | ")?.map { it.trim() }?.filter { it.isNotBlank() } ?: emptyList()

/**
 * toProductLabelType
 * Maps a backend label string to the matching [ProductLabelType] enum value.
 * Returns null if the string is blank or doesn't match any known label.
 *
 * @return Matching label variant or null (no badge rendered).
 */
internal fun String?.toProductLabelType(): ProductLabelType? = when (this?.trim()?.uppercase()) {
    "BEST PICK"      -> ProductLabelType.BEST_PICK
    "BEST DESIGN"    -> ProductLabelType.BEST_DESIGN
    "BALANCED"       -> ProductLabelType.BALANCED
    "EDITOR'S CHOICE" -> ProductLabelType.EDITORS_CHOICE
    "BUDGET PICK"    -> ProductLabelType.BUDGET_PICK
    else             -> null
}

/**
 * toBuyInOptions
 * Builds the ordered [BuyInOption] list from available shop fields.
 * Only includes a platform when its link field is non-null and non-blank.
 */
internal fun ProductResponse.toBuyInOptions(): List<BuyInOption> = buildList {
    if (!shopeeLink.isNullOrBlank()) add(BuyInOption(
        logoSrc = ProductStatics.IC_SHOPEE,
        price = "${ProductStatics.PRICE_PESO}${shopeePrice.orEmpty()}",
        discount = shopeePromo.orEmpty(),
        onClick = { window.open(shopeeLink, "_blank") },
    ))
    if (!lazadaLink.isNullOrBlank()) add(BuyInOption(
        logoSrc = ProductStatics.IC_LAZADA,
        price = "${ProductStatics.PRICE_PESO}${lazadaPrice.orEmpty()}",
        discount = lazadaPromo.orEmpty(),
        onClick = { window.open(lazadaLink, "_blank") },
    ))
    if (!amazonLink.isNullOrBlank()) add(BuyInOption(
        logoSrc = ProductStatics.IC_AMAZON,
        price = "${ProductStatics.PRICE_DOLLAR}${amazonPrice.orEmpty()}",
        discount = amazonPromo.orEmpty(),
        onClick = { window.open(amazonLink, "_blank") },
    ))
    if (!tiktokLink.isNullOrBlank()) add(BuyInOption(
        logoSrc = ProductStatics.IC_TIKTOK_SHOP,
        price = "${ProductStatics.PRICE_PESO}${tiktokPrice.orEmpty()}",
        discount = tiktokPromo.orEmpty(),
        onClick = { window.open(tiktokLink, "_blank") },
    ))
}
